import { useMemo, useCallback } from 'react';
import { TradeType, BrokerageRates, ChargesBreakdown } from '../types';
import { CHARGE_RATES } from '../utils/constants';

// ============================================================================
// TRADE CALCULATION TYPES
// ============================================================================

export interface TradeInputs {
  tradeType: TradeType;
  purchaseLots: number;
  lotSize: number;
  purchasePrice: number;
  saleLots: number;
  salePrice: number;
  currentPrice: number;
  direction?: 'BUY' | 'SELL';
}

export interface TradeCalculations {
  balanceLots: number;
  status: 'OPEN' | 'CLOSED' | '';
  investedAmount: number;
  saleValue: number;
  realizedPnL: number;
  unrealizedPnL: number;
  totalPnL: number;
  pnlPercentage: number;
  currentValue: number;
  brokerageAmount: number;
  chargesBreakdown?: ChargesBreakdown;
}

// ============================================================================
// ITEMIZED CHARGES CALCULATION
// ============================================================================

export function calculateChargesBreakdown(
  tradeType: TradeType,
  buyValue: number,
  sellValue: number,
  brokerageRate: number,
  isIntraday: boolean,
  exchange: 'NSE' | 'BSE' = 'NSE',
  fixedBrokerage?: number,
): ChargesBreakdown {
  const totalTurnover = buyValue + sellValue;

  // 1. Brokerage
  let brokerage = 0;
  const isEquityType = tradeType === TradeType.STOCK || tradeType === TradeType.EQUITY;
  const isIntradayType = tradeType === TradeType.INTRA_STOCK || (isEquityType && isIntraday);

  if (fixedBrokerage !== undefined) {
    // Options/any: fixed brokerage amount passed directly (e.g. ₹X per lot × N lots)
    brokerage = fixedBrokerage;
  } else {
    switch (tradeType) {
      case TradeType.STOCK:
      case TradeType.EQUITY:
      case TradeType.INTRA_STOCK:
        brokerage = totalTurnover * brokerageRate;
        break;
      case TradeType.FUTURE:
        brokerage = totalTurnover * (brokerageRate || CHARGE_RATES.DEFAULT_FUTURE_BROKERAGE);
        break;
      case TradeType.OPTION:
        // OPTIONS MUST use fixedBrokerage (per-lot). If caller forgot, brokerage = 0
        // to make the mistake obvious. NEVER use percentage of turnover for options.
        brokerage = 0;
        break;
    }
  }

  // 2. STT
  let stt = 0;
  if (isEquityType && !isIntraday) {
    stt = (buyValue * CHARGE_RATES.STT_EQUITY_DELIVERY_BUY) + (sellValue * CHARGE_RATES.STT_EQUITY_DELIVERY_SELL);
  } else if (isIntradayType) {
    stt = sellValue * CHARGE_RATES.STT_EQUITY_INTRADAY_SELL;
  } else if (tradeType === TradeType.FUTURE) {
    stt = sellValue * CHARGE_RATES.STT_FUTURE_SELL;
  } else if (tradeType === TradeType.OPTION) {
    stt = (buyValue * CHARGE_RATES.STT_OPTION_BUY) + (sellValue * CHARGE_RATES.STT_OPTION_SELL);
  }

  // 3. Exchange Transaction Charges (NSE vs BSE)
  const isBSE = exchange === 'BSE';
  let exchangeCharges = 0;
  if (isEquityType || isIntradayType) {
    exchangeCharges = totalTurnover * (isBSE ? CHARGE_RATES.EXCHANGE_EQUITY_BSE : CHARGE_RATES.EXCHANGE_EQUITY_NSE);
  } else if (tradeType === TradeType.FUTURE) {
    exchangeCharges = totalTurnover * (isBSE ? CHARGE_RATES.EXCHANGE_FUTURE_BSE : CHARGE_RATES.EXCHANGE_FUTURE_NSE);
  } else if (tradeType === TradeType.OPTION) {
    exchangeCharges = totalTurnover * CHARGE_RATES.EXCHANGE_OPTION;
  }

  // 4. SEBI Charges
  const sebiCharges = totalTurnover * CHARGE_RATES.SEBI_RATE;

  // 5. Stamp Duty (BUY side only)
  let stampDuty = 0;
  if (isEquityType && !isIntraday) {
    stampDuty = buyValue * CHARGE_RATES.STAMP_EQUITY_DELIVERY;
  } else if (isIntradayType) {
    stampDuty = buyValue * CHARGE_RATES.STAMP_EQUITY_INTRADAY;
  } else if (tradeType === TradeType.FUTURE) {
    stampDuty = buyValue * CHARGE_RATES.STAMP_FUTURE;
  } else if (tradeType === TradeType.OPTION) {
    stampDuty = buyValue * CHARGE_RATES.STAMP_OPTION;
  }

  // 6. GST: 18% on (brokerage + exchangeCharges + sebiCharges)
  const gst = (brokerage + exchangeCharges + sebiCharges) * CHARGE_RATES.GST_RATE;

  const totalCharges = brokerage + stt + exchangeCharges + sebiCharges + stampDuty + gst;

  // 7-decimal precision (sub-paise). Must match backend
  // brokerage.service.ts:calculateItemizedCharges so UI previews agree with
  // stored values.
  const P = 10000000;
  return {
    brokerage: Math.round(brokerage * P) / P,
    stt: Math.round(stt * P) / P,
    exchangeCharges: Math.round(exchangeCharges * P) / P,
    sebiCharges: Math.round(sebiCharges * P) / P,
    stampDuty: Math.round(stampDuty * P) / P,
    gst: Math.round(gst * P) / P,
    totalCharges: Math.round(totalCharges * P) / P,
  };
}

// ============================================================================
// LIFO ENGINE & BREAKEVEN CALCULATOR
// ============================================================================

export interface TradeLeg {
  tradeId: string;
  lots: number;        // purchase lots (shares for equity, lots for F&O)
  lotSize: number;
  price: number;       // purchase price
  date: string;        // buy date (ISO string)
  tradeType: TradeType;
  saleLots?: number;
  salePrice?: number;
  direction?: 'BUY' | 'SELL';
}

export interface LIFOResult {
  remainingLegs: { leg: TradeLeg; remainingQty: number }[];
  weightedAvgBuyPrice: number;
  totalRemainingShares: number;
  totalRemainingCost: number;
  soldAllocations: { leg: TradeLeg; soldQty: number }[];
}

/**
 * Apply LIFO (Last In First Out) selling to trade legs.
 * Newest buys are sold first.
 */
export function applyLIFO(buyLegs: TradeLeg[], totalSoldShares: number): LIFOResult {
  // Sort by date DESCENDING (newest first = LIFO)
  const sorted = [...buyLegs].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  let remaining = totalSoldShares;
  const soldAllocations: { leg: TradeLeg; soldQty: number }[] = [];
  const remainingLegs: { leg: TradeLeg; remainingQty: number }[] = [];

  for (const leg of sorted) {
    const totalShares = leg.lots * leg.lotSize;
    if (remaining >= totalShares) {
      // Entire leg is sold
      soldAllocations.push({ leg, soldQty: totalShares });
      remaining -= totalShares;
    } else if (remaining > 0) {
      // Partial sale
      soldAllocations.push({ leg, soldQty: remaining });
      remainingLegs.push({ leg, remainingQty: totalShares - remaining });
      remaining = 0;
    } else {
      // Not sold at all
      remainingLegs.push({ leg, remainingQty: totalShares });
    }
  }

  // Sort remaining back by date ascending (oldest first for display)
  remainingLegs.sort((a, b) => new Date(a.leg.date).getTime() - new Date(b.leg.date).getTime());

  const totalRemainingShares = remainingLegs.reduce((sum, r) => sum + r.remainingQty, 0);
  const totalRemainingCost = remainingLegs.reduce((sum, r) => sum + r.remainingQty * r.leg.price, 0);
  const weightedAvgBuyPrice = totalRemainingShares > 0 ? totalRemainingCost / totalRemainingShares : 0;

  return {
    remainingLegs,
    weightedAvgBuyPrice: Math.round(weightedAvgBuyPrice * 1000) / 1000,
    totalRemainingShares,
    totalRemainingCost: Math.round(totalRemainingCost * 1000) / 1000,
    soldAllocations,
  };
}

// ============================================================================
// BREAKEVEN CALCULATOR
// ============================================================================

export interface BreakevenResult {
  // Equity: 4 scenarios
  intradayBE?: number;      // BE for today's intraday shares only (0.03% brokerage)
  deliveryBE?: number;      // BE for older (non-today) delivery shares only (0.3% brokerage)
  fullClearBE?: number;     // BE to sell ALL shares today (intra portion at 0.03%, delivery portion at 0.3%)
  carryForwardBE?: number;  // BE if ALL shares carried forward and sold as delivery (0.3% brokerage)
  // Share split info (equity only)
  todayShares?: number;     // shares bought today (intraday-eligible)
  todayCost?: number;       // total cost of today's shares
  olderShares?: number;     // shares bought on previous days (delivery)
  olderCost?: number;       // total cost of older shares
  // F&O: 1 each
  futureBE?: number;
  optionBE?: number;
}

/**
 * Calculate breakeven sell price — the minimum price to sell at to not lose money
 * after all charges (brokerage buy+sell, STT, exchange, SEBI, stamp, GST).
 *
 * Uses iterative convergence since sell charges depend on sell price (circular).
 */
export function calculateBreakevenRate(
  totalShares: number,
  totalBuyCost: number,
  buyCharges: number,
  tradeType: TradeType,
  brokerageRate: number,
  isIntraday: boolean,
  maxIterations: number = 20,
  tolerance: number = 0.001,
  fixedBrokerage?: number,
  direction: 'BUY' | 'SELL' = 'BUY',
): number {
  if (totalShares <= 0) return 0;

  const avgPrice = totalBuyCost / totalShares;

  if (direction === 'SELL') {
    // Sell-first: find max cover (buy-back) price
    // totalBuyCost = total open sell revenue, buyCharges = open sell charges
    let BE = avgPrice * 0.995; // start below avg sell price

    for (let i = 0; i < maxIterations; i++) {
      const estimatedCoverValue = BE * totalShares;
      // Cover is a buy, so pass as buyValue
      const coverCharges = calculateChargesBreakdown(tradeType, estimatedCoverValue, 0, brokerageRate, isIntraday, 'NSE', fixedBrokerage);
      const requiredMaxCoverValue = totalBuyCost - buyCharges - coverCharges.totalCharges;
      const newBE = requiredMaxCoverValue / totalShares;

      if (Math.abs(newBE - BE) < tolerance) {
        BE = newBE;
        break;
      }
      BE = newBE;
    }

    return Math.round(BE * 1000) / 1000;
  }

  // BUY direction (default): find min sell price
  let BE = avgPrice * 1.005;

  for (let i = 0; i < maxIterations; i++) {
    const estimatedSellValue = BE * totalShares;
    const sellCharges = calculateChargesBreakdown(tradeType, 0, estimatedSellValue, brokerageRate, isIntraday, 'NSE', fixedBrokerage);
    const requiredSellValue = totalBuyCost + buyCharges + sellCharges.totalCharges;
    const newBE = requiredSellValue / totalShares;

    if (Math.abs(newBE - BE) < tolerance) {
      BE = newBE;
      break;
    }
    BE = newBE;
  }

  return Math.round(BE * 1000) / 1000;
}

/**
 * Compute breakeven for a merged position.
 * For equity: 3 scenarios. For Future/Option: 1 each.
 */
export function computeBreakevenForPosition(
  remainingLegs: { leg: TradeLeg; remainingQty: number }[],
  brokerageRates: BrokerageRates,
  primaryTradeType: TradeType,
  clientName?: string,
  direction: 'BUY' | 'SELL' = 'BUY',
): BreakevenResult {
  const totalShares = remainingLegs.reduce((sum, r) => sum + r.remainingQty, 0);
  const totalCost = remainingLegs.reduce((sum, r) => sum + r.remainingQty * r.leg.price, 0);

  if (totalShares <= 0) return {};

  const isSellFirst = direction === 'SELL';
  const isEquity = primaryTradeType === TradeType.STOCK || primaryTradeType === TradeType.EQUITY || primaryTradeType === TradeType.INTRA_STOCK;

  if (isEquity) {
    // Split legs into today (intraday) vs older (delivery)
    const now = new Date();
    const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    let todayShares = 0, todayCost = 0, olderShares = 0, olderCost = 0;
    for (const r of remainingLegs) {
      const d = new Date(r.leg.date);
      const legDate = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      if (legDate === today) {
        todayShares += r.remainingQty;
        todayCost += r.remainingQty * r.leg.price;
      } else {
        olderShares += r.remainingQty;
        olderCost += r.remainingQty * r.leg.price;
      }
    }

    // For sell-first: open side is a sell, so charges calc uses sellValue
    // For buy-first: open side is a buy, so charges calc uses buyValue

    // 1. Intraday BE
    let intradayBE: number | undefined;
    if (todayShares > 0) {
      const todayOpenCharges = isSellFirst
        ? calculateChargesBreakdown(TradeType.INTRA_STOCK, 0, todayCost, brokerageRates.intraStock, true)
        : calculateChargesBreakdown(TradeType.INTRA_STOCK, todayCost, 0, brokerageRates.intraStock, true);
      intradayBE = calculateBreakevenRate(todayShares, todayCost, todayOpenCharges.totalCharges, TradeType.INTRA_STOCK, brokerageRates.intraStock, true, 20, 0.001, undefined, direction);
    }

    // 2. Delivery BE
    let deliveryBE: number | undefined;
    if (olderShares > 0) {
      const olderOpenCharges = isSellFirst
        ? calculateChargesBreakdown(TradeType.EQUITY, 0, olderCost, brokerageRates.stock, false)
        : calculateChargesBreakdown(TradeType.EQUITY, olderCost, 0, brokerageRates.stock, false);
      deliveryBE = calculateBreakevenRate(olderShares, olderCost, olderOpenCharges.totalCharges, TradeType.EQUITY, brokerageRates.stock, false, 20, 0.001, undefined, direction);
    }

    // 3. Carry Forward BE
    const cfOpenCharges = isSellFirst
      ? calculateChargesBreakdown(TradeType.EQUITY, 0, totalCost, brokerageRates.stock, false)
      : calculateChargesBreakdown(TradeType.EQUITY, totalCost, 0, brokerageRates.stock, false);
    const carryForwardBE = calculateBreakevenRate(totalShares, totalCost, cfOpenCharges.totalCharges, TradeType.EQUITY, brokerageRates.stock, false, 20, 0.001, undefined, direction);

    // 4. Full Clear BE
    let fullClearBE: number;
    if (todayShares > 0 && olderShares > 0) {
      const todayOpenCh = isSellFirst
        ? calculateChargesBreakdown(TradeType.INTRA_STOCK, 0, todayCost, brokerageRates.intraStock, true).totalCharges
        : calculateChargesBreakdown(TradeType.INTRA_STOCK, todayCost, 0, brokerageRates.intraStock, true).totalCharges;
      const olderOpenCh = isSellFirst
        ? calculateChargesBreakdown(TradeType.EQUITY, 0, olderCost, brokerageRates.stock, false).totalCharges
        : calculateChargesBreakdown(TradeType.EQUITY, olderCost, 0, brokerageRates.stock, false).totalCharges;
      const todayBEVal = calculateBreakevenRate(todayShares, todayCost, todayOpenCh, TradeType.INTRA_STOCK, brokerageRates.intraStock, true, 20, 0.001, undefined, direction);
      const olderBEVal = calculateBreakevenRate(olderShares, olderCost, olderOpenCh, TradeType.EQUITY, brokerageRates.stock, false, 20, 0.001, undefined, direction);
      fullClearBE = Math.round(((todayBEVal * todayShares + olderBEVal * olderShares) / totalShares) * 1000) / 1000;
    } else if (todayShares > 0) {
      fullClearBE = intradayBE!;
    } else {
      fullClearBE = deliveryBE!;
    }

    return { intradayBE, deliveryBE, fullClearBE, carryForwardBE, todayShares, todayCost, olderShares, olderCost };
  }

  if (primaryTradeType === TradeType.FUTURE) {
    // For sell-first: open is a sell
    const openCharges = isSellFirst
      ? calculateChargesBreakdown(TradeType.FUTURE, 0, totalCost, CHARGE_RATES.DEFAULT_FUTURE_BROKERAGE, false)
      : calculateChargesBreakdown(TradeType.FUTURE, totalCost, 0, CHARGE_RATES.DEFAULT_FUTURE_BROKERAGE, false);
    const futureBE = calculateBreakevenRate(totalShares, totalCost, openCharges.totalCharges, TradeType.FUTURE, CHARGE_RATES.DEFAULT_FUTURE_BROKERAGE, false, 20, 0.001, undefined, direction);
    return { futureBE };
  }

  if (primaryTradeType === TradeType.OPTION) {
    const totalLots = remainingLegs.reduce((sum, r) => {
      const legLotSize = r.leg.lotSize || 1;
      return sum + Math.round(r.remainingQty / legLotSize);
    }, 0);
    const cName = (clientName || '').toUpperCase().trim();
    const perLotRate = OPTION_BROKERAGE_PER_LOT[cName] || DEFAULT_OPTION_BROKERAGE;
    const fixedBrk = perLotRate * totalLots;
    // For sell-first: open is a sell
    const openCharges = isSellFirst
      ? calculateChargesBreakdown(TradeType.OPTION, 0, totalCost, 0, false, 'NSE', fixedBrk)
      : calculateChargesBreakdown(TradeType.OPTION, totalCost, 0, 0, false, 'NSE', fixedBrk);
    const optionBE = calculateBreakevenRate(totalShares, totalCost, openCharges.totalCharges, TradeType.OPTION, 0, false, 20, 0.001, fixedBrk, direction);
    return { optionBE };
  }

  return {};
}

// ============================================================================
// LEGACY OPTION BROKERAGE (per-lot for specific clients)
// ============================================================================

// Legacy: option brokerage is now per-client via client.brokerageRates.option
// These exports kept for backward compatibility but should not be used for new code
export const OPTION_BROKERAGE_PER_LOT: Record<string, number> = {};
export const DEFAULT_OPTION_BROKERAGE = 100;

export function getOptionBrokerage(clientName: string, lots: number): number {
  const perLot = OPTION_BROKERAGE_PER_LOT[clientName.toUpperCase()] || DEFAULT_OPTION_BROKERAGE;
  return perLot * lots;
}

// ============================================================================
// CORE CALCULATION
// ============================================================================

function calculateAll(
  inputs: TradeInputs,
  clientName: string,
  brokerageRates: BrokerageRates,
): TradeCalculations {
  const { tradeType, purchaseLots, lotSize, purchasePrice, saleLots, salePrice, currentPrice } = inputs;
  const direction = inputs.direction || 'BUY';
  const isSellFirst = direction === 'SELL';

  const balanceLots = purchaseLots - saleLots;
  let status: 'OPEN' | 'CLOSED' | '' = '';
  if (balanceLots > 0) status = 'OPEN';
  else if (balanceLots === 0 && saleLots > 0) status = 'CLOSED';

  const investedAmount = balanceLots * lotSize * purchasePrice;
  const saleValue = salePrice * saleLots * lotSize;
  const soldLotsInvested = purchasePrice * saleLots * lotSize;

  // Calculate charges using itemized breakdown
  let brokerageAmount = 0;
  let chargesBreakdown: ChargesBreakdown | undefined;

  if (saleLots > 0) {
    if (tradeType === TradeType.OPTION) {
      // Options: use legacy per-lot brokerage for backward compat
      brokerageAmount = getOptionBrokerage(clientName, saleLots);
    } else {
      let rate: number;
      let isIntraday = false;
      switch (tradeType) {
        case TradeType.STOCK:
        case TradeType.EQUITY:
          rate = brokerageRates.stock;
          break;
        case TradeType.INTRA_STOCK:
          rate = brokerageRates.intraStock;
          isIntraday = true;
          break;
        case TradeType.FUTURE:
          rate = CHARGE_RATES.DEFAULT_FUTURE_BROKERAGE;
          break;
        default:
          rate = brokerageRates.stock;
      }
      // For sell-first: open is sell (soldLotsInvested=open sell revenue), close is buy (saleValue=cover cost)
      // Swap buyValue/sellValue in charges calc
      if (isSellFirst) {
        chargesBreakdown = calculateChargesBreakdown(tradeType, saleValue, soldLotsInvested, rate, isIntraday);
      } else {
        chargesBreakdown = calculateChargesBreakdown(tradeType, soldLotsInvested, saleValue, rate, isIntraday);
      }
      brokerageAmount = chargesBreakdown.totalCharges;
    }
  }

  // Realized P&L
  let realizedPnL = 0;
  if (saleLots > 0) {
    if (isSellFirst) {
      // Sell-first: profit = open sell revenue - cover cost - charges
      realizedPnL = soldLotsInvested - saleValue - brokerageAmount;
    } else {
      // Buy-first: profit = sale proceeds - purchase cost - charges
      realizedPnL = saleValue - soldLotsInvested - brokerageAmount;
    }
  }

  // Unrealized P&L
  let unrealizedPnL = 0;
  if (balanceLots > 0) {
    const currentValue = currentPrice * balanceLots * lotSize;
    if (isSellFirst) {
      // Sell-first: profit when price drops (invested = open sell revenue for remaining)
      unrealizedPnL = investedAmount - currentValue;
    } else {
      unrealizedPnL = currentValue - investedAmount;
    }
  }

  const totalPnL = realizedPnL + unrealizedPnL;
  const totalOriginalInvestment = purchaseLots * lotSize * purchasePrice;
  const pnlPercentage = totalOriginalInvestment > 0 ? (totalPnL / totalOriginalInvestment) * 100 : 0;
  const currentValue = balanceLots > 0 ? currentPrice * balanceLots * lotSize : 0;

  return {
    balanceLots,
    status,
    investedAmount,
    saleValue,
    realizedPnL,
    unrealizedPnL,
    totalPnL,
    pnlPercentage,
    currentValue,
    brokerageAmount,
    chargesBreakdown,
  };
}

// ============================================================================
// HOOKS
// ============================================================================

export function useTradeCalculations(
  inputs: TradeInputs,
  clientName: string,
  brokerageRates: BrokerageRates
): TradeCalculations {
  return useMemo(() => calculateAll(inputs, clientName, brokerageRates), [inputs, clientName, brokerageRates]);
}

export function useTradePreview() {
  const calculatePreview = useCallback(
    (
      tradeType: TradeType,
      purchaseLots: number,
      lotSize: number,
      purchasePrice: number,
      saleLots = 0,
      salePrice = 0,
      currentPrice = 0,
      clientName = '',
      brokerageRates: BrokerageRates = { stock: 0.003, option: 100, intraStock: 0.0003 },
      direction: 'BUY' | 'SELL' = 'BUY',
    ): TradeCalculations => {
      return calculateAll(
        {
          tradeType,
          purchaseLots: purchaseLots || 0,
          lotSize: lotSize || 0,
          purchasePrice: purchasePrice || 0,
          saleLots: saleLots || 0,
          salePrice: salePrice || 0,
          currentPrice: currentPrice || purchasePrice || 0,
          direction,
        },
        clientName,
        brokerageRates,
      );
    },
    []
  );

  return { calculatePreview };
}

export default useTradeCalculations;
